import type { ShowConsultation } from '@/components/PatientPageV2/components/ConsultationTabSections/ShowConsultation';
import { useConsultActions } from '@/hooks/consult/useConsultActions';
import type { SaveConsultationFunc } from '@/hooks/consult/useSaveConsultation';
import type { Consultation } from '@/hooks/patients/usePatientConsultations';
import { useAppStore } from '@/state-management';
import Box from '@mui/material/Box';
import { useCallback } from 'react';
import { PrescriptionAssistantTool } from '../../components/PrescriptionAssistantTool/PrescriptionAssistantTool';
import type { PrescriptionAssistantToolDetails } from '../../components/PrescriptionAssistantTool/PrescriptionAssistantTool.types';
import { closeBrowserWindow } from '../utils/closeBrowserWindow';

interface PrescriptionsProps {
  prescriptionDetails: PrescriptionAssistantToolDetails;
  isPostingPrescription: boolean;
  prescribeStateError: boolean;
  onPrescriptionErrorClosed: () => void;
  consultation: Consultation | null;
  isConcessionPatient: boolean;
  isPatientActive: boolean;
  saveConsult: SaveConsultationFunc;
  setIsProcessingAction: React.Dispatch<React.SetStateAction<boolean>>;
}

export const Prescriptions = ({
  prescriptionDetails,
  isPostingPrescription,
  prescribeStateError,
  onPrescriptionErrorClosed,
  consultation,
  isConcessionPatient,
  isPatientActive,
  saveConsult,
  setIsProcessingAction,
}: PrescriptionsProps) => {
  const store = useAppStore();
  const isComplimentary = store.guidedDoctorConsult.fields.complimentary;
  const followUpWeekPeriod = store.guidedDoctorConsult.fields.followUpWeekPeriod;
  const { email: patientEmail } = store.getPatientContactDetails();

  const { onOnlyCancelComplete } = useConsultActions({
    consult: consultation as ShowConsultation,
    isConcessionPatient,
    patientActive: isPatientActive,
    patientEmail: patientEmail,
    isComplimentary,
    onPrescriptionActionComplete: closeBrowserWindow,
    saveConsult: saveConsult,
    submitCreatePrescription: () => Promise.resolve(null),
    nextConsultationInWeeks: followUpWeekPeriod ?? undefined,
  });

  const handleCompleteAndClose = useCallback(async () => {
    setIsProcessingAction(true);
    try {
      await onOnlyCancelComplete();
    } finally {
      setIsProcessingAction(false);
    }
  }, [onOnlyCancelComplete, setIsProcessingAction]);

  return (
    <Box>
      <PrescriptionAssistantTool
        prescriptionDetails={prescriptionDetails}
        isPostingPrescription={isPostingPrescription}
        showPrescribeStateError={prescribeStateError}
        onPrescriptionErrorClosed={onPrescriptionErrorClosed}
        onOnlyCancelComplete={handleCompleteAndClose}
      />
    </Box>
  );
};
